import { useRef, useCallback, useEffect, useState } from 'react';
import styles from './RichTextarea.module.css';
import { markdownToHtml, normalizeEditorHtml } from './RichTextarea.utils';

export interface RichTextareaProps {
	value: string;
	onChange: (html: string) => void;
	placeholder?: string;
	readOnly?: boolean;
	hasError?: boolean;
}

interface FormatState {
	bold: boolean;
	italic: boolean;
	underline: boolean;
	unorderedList: boolean;
	orderedList: boolean;
}

const INITIAL_FORMATS: FormatState = {
	bold: false,
	italic: false,
	underline: false,
	unorderedList: false,
	orderedList: false,
};

export const RichTextarea = ({
	value,
	onChange,
	placeholder,
	readOnly,
	hasError,
}: RichTextareaProps) => {
	const editorRef = useRef<HTMLDivElement>(null);
	const [formats, setFormats] = useState<FormatState>(INITIAL_FORMATS);
	const isInternalChange = useRef(false);

	useEffect(() => {
		if (readOnly) return;
		const editor = editorRef.current;
		if (!editor) return;
		if (isInternalChange.current) {
			isInternalChange.current = false;
			return;
		}
		const html = markdownToHtml(value ?? '');
		if (editor.innerHTML !== html) {
			const sel = globalThis.window.getSelection();
			let savedRange: Range | null = null;
			if (sel && sel.rangeCount > 0 && editor.contains(sel.anchorNode)) {
				savedRange = sel.getRangeAt(0).cloneRange();
			}
			editor.innerHTML = html;
			if (savedRange) {
				sel?.removeAllRanges();
				sel?.addRange(savedRange);
			}
		}
	}, [value, readOnly]);

	const updateFormats = useCallback(() => {
		setFormats({
			bold: document.queryCommandState('bold'),
			italic: document.queryCommandState('italic'),
			underline: document.queryCommandState('underline'),
			unorderedList: document.queryCommandState('insertUnorderedList'),
			orderedList: document.queryCommandState('insertOrderedList'),
		});
	}, []);

	useEffect(() => {
		const onSelectionChange = () => {
			if (editorRef.current?.contains(document.getSelection()?.anchorNode ?? null)) {
				updateFormats();
			}
		};
		document.addEventListener('selectionchange', onSelectionChange);
		return () => document.removeEventListener('selectionchange', onSelectionChange);
	}, [updateFormats]);

	const exec = useCallback(
		(command: string, val?: string) => {
			document.execCommand(command, false, val);
			editorRef.current?.focus();
			if (editorRef.current) {
				isInternalChange.current = true;
				onChange(normalizeEditorHtml(editorRef.current.innerHTML));
			}
			updateFormats();
		},
		[onChange, updateFormats]
	);

	const handleInput = useCallback(() => {
		if (editorRef.current) {
			isInternalChange.current = true;
			onChange(normalizeEditorHtml(editorRef.current.innerHTML));
		}
	}, [onChange]);

	if (readOnly) {
		if (!value || value === '<br>') {
			return <span className={styles.viewText}>Sem observações.</span>;
		}
		const html = markdownToHtml(value);
		return <div className={styles.viewText} dangerouslySetInnerHTML={{ __html: html }} />;
	}

	const btn = (active: boolean) =>
		[styles.toolbarBtn, active ? styles.toolbarBtnActive : ''].filter(Boolean).join(' ');

	return (
		<div className={[styles.wrapper, hasError && styles.error].filter(Boolean).join(' ')}>
			<div className={styles.toolbar}>
				<button
					type="button"
					className={btn(formats.bold)}
					onMouseDown={(e) => {
						e.preventDefault();
						exec('bold');
					}}
					title="Negrito"
				>
					<b>B</b>
				</button>
				<button
					type="button"
					className={btn(formats.italic)}
					onMouseDown={(e) => {
						e.preventDefault();
						exec('italic');
					}}
					title="Itálico"
				>
					<i>I</i>
				</button>
				<button
					type="button"
					className={btn(formats.underline)}
					onMouseDown={(e) => {
						e.preventDefault();
						exec('underline');
					}}
					title="Sublinhado"
				>
					<u>U</u>
				</button>
				<button
					type="button"
					className={styles.toolbarBtn}
					onMouseDown={(e) => {
						e.preventDefault();
						exec('removeFormat');
					}}
					title="Limpar formatação"
				>
					⊘
				</button>
				<div className={styles.divider} />
				<button
					type="button"
					className={btn(formats.unorderedList)}
					onMouseDown={(e) => {
						e.preventDefault();
						exec('insertUnorderedList');
					}}
					title="Lista não-ordenada"
				>
					<svg viewBox="0 0 16 16" fill="currentColor">
						<rect x="0" y="2" width="2" height="2" />
						<rect x="4" y="2" width="12" height="2" />
						<rect x="0" y="7" width="2" height="2" />
						<rect x="4" y="7" width="12" height="2" />
						<rect x="0" y="12" width="2" height="2" />
						<rect x="4" y="12" width="12" height="2" />
					</svg>
				</button>
				<button
					type="button"
					className={btn(formats.orderedList)}
					onMouseDown={(e) => {
						e.preventDefault();
						exec('insertOrderedList');
					}}
					title="Lista ordenada"
				>
					<svg viewBox="0 0 16 16" fill="currentColor">
						<text x="0" y="5" fontSize="6" fontFamily="sans-serif">
							1.
						</text>
						<rect x="6" y="2" width="10" height="2" />
						<text x="0" y="10" fontSize="6" fontFamily="sans-serif">
							2.
						</text>
						<rect x="6" y="7" width="10" height="2" />
						<text x="0" y="15" fontSize="6" fontFamily="sans-serif">
							3.
						</text>
						<rect x="6" y="12" width="10" height="2" />
					</svg>
				</button>
				<div className={styles.divider} />
				<button
					type="button"
					className={styles.toolbarBtn}
					onMouseDown={(e) => {
						e.preventDefault();
						exec('outdent');
					}}
					title="Recuo"
				>
					<svg viewBox="0 0 16 16" fill="currentColor">
						<polygon points="0,4 5,8 0,12" />
						<rect x="7" y="2" width="9" height="2" />
						<rect x="7" y="7" width="9" height="2" />
						<rect x="7" y="12" width="9" height="2" />
					</svg>
				</button>
				<button
					type="button"
					className={styles.toolbarBtn}
					onMouseDown={(e) => {
						e.preventDefault();
						exec('indent');
					}}
					title="Avanço"
				>
					<svg viewBox="0 0 16 16" fill="currentColor">
						<polygon
							points="0,4 5,8 0,12"
							transform="translate(5,0) scale(-1,1) translate(-10,0)"
						/>
						<rect x="0" y="2" width="9" height="2" />
						<rect x="0" y="7" width="9" height="2" />
						<rect x="0" y="12" width="9" height="2" />
					</svg>
				</button>
			</div>
			<div
				ref={editorRef}
				className={styles.editor}
				contentEditable
				suppressContentEditableWarning
				data-placeholder={placeholder}
				onInput={handleInput}
			/>
		</div>
	);
};
